import {
  type ApprovalRequestReadModel,
  type ExecutePaidOrderItemReturnCommand,
  isExecutePaidOrderItemReturnCommand,
  isPaidSaleReturnOriginalPayment,
} from "@billiard/contracts";
import type { PaidOrderItemReturnExecutionExpected } from "./api";

export interface PaidOrderItemReturnExecutionRecovery {
  tenantId: string;
  storeId: string;
  accountId: string;
  command: ExecutePaidOrderItemReturnCommand;
  expected: PaidOrderItemReturnExecutionExpected;
  state: "CLAIMED" | "OUTCOME_UNKNOWN";
}

export type PaidOrderItemReturnExecutionRecoveryView =
  | { status: "NONE" }
  | { status: "OWNED"; recovery: PaidOrderItemReturnExecutionRecovery }
  | { status: "RECOVERY_STORAGE_UNAVAILABLE" };

export const PAID_ORDER_ITEM_RETURN_EXECUTION_RECOVERY_STORAGE_KEY =
  "billiard-erp:paid-order-item-return-execution-recovery:v2";
export const PAID_ORDER_ITEM_RETURN_EXECUTION_RECOVERY_LEGACY_STORAGE_KEY =
  "billiard-erp:paid-order-item-return-execution-recovery:v1";

const pendingByIdentity = new Map<string, PaidOrderItemReturnExecutionRecovery>();
const dispatchingByIdentity = new Map<string, PaidOrderItemReturnExecutionRecovery>();
let hydrated = false;
let unavailable = false;

function record(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

function exactKeys(value: Record<string, unknown>, keys: readonly string[]): boolean {
  return (
    Object.keys(value).length === keys.length && keys.every((key) => Object.hasOwn(value, key))
  );
}

function bounded(value: unknown, maximum: number): value is string {
  return (
    typeof value === "string" &&
    value === value.trim() &&
    value.length > 0 &&
    [...value].length <= maximum
  );
}

function validExpected(value: unknown): value is PaidOrderItemReturnExecutionExpected {
  const expectedKeys = [
    "approvalSnapshotVersion",
    "itemId",
    "returnQuantity",
    "amountFen",
    "paymentMethod",
    "refundExecutionMode",
    "returnedQuantityAfter",
    "physicalDisposition",
    "inventoryEffect",
    "inventoryQuantityBefore",
    "inventoryQuantityAfter",
    "inventorySnapshotKnown",
    ...(record(value) && Object.hasOwn(value, "refundAllocations") ? ["refundAllocations"] : []),
  ];
  if (!record(value) || !exactKeys(value, expectedKeys)) {
    return false;
  }
  const allocations = value.refundAllocations;
  const combinedRefund = Array.isArray(allocations);
  const allocationPlanValid = combinedRefund
    ? (value.approvalSnapshotVersion === 3 || value.approvalSnapshotVersion === 4) &&
      value.paymentMethod === null &&
      value.refundExecutionMode === null &&
      allocations.length >= (value.approvalSnapshotVersion === 4 ? 1 : 2) &&
      allocations.length <= 32 &&
      allocations.every(isPaidSaleReturnOriginalPayment) &&
      new Set(allocations.map((allocation) => allocation.paymentId)).size === allocations.length &&
      allocations.reduce((total, allocation) => total + allocation.refundAmountFen, 0) ===
        value.amountFen
    : !Object.hasOwn(value, "refundAllocations") &&
      (value.approvalSnapshotVersion === 1 || value.approvalSnapshotVersion === 2) &&
      value.paymentMethod !== null &&
      value.refundExecutionMode !== null;
  const inventoryExpectationValid =
    value.approvalSnapshotVersion === 4
      ? value.returnQuantity === 1 &&
        value.returnedQuantityAfter === 1 &&
        value.inventoryQuantityBefore === null &&
        value.inventoryQuantityAfter === null
      : value.inventorySnapshotKnown
        ? value.physicalDisposition === "SALEABLE_RETURNED"
          ? Number.isSafeInteger(value.inventoryQuantityBefore) &&
            (value.inventoryQuantityBefore as number) >= 0 &&
            Number.isSafeInteger(value.inventoryQuantityAfter) &&
            value.inventoryQuantityAfter ===
              (value.inventoryQuantityBefore as number) + (value.returnQuantity as number)
          : value.approvalSnapshotVersion === 1
            ? Number.isSafeInteger(value.inventoryQuantityBefore) &&
              (value.inventoryQuantityBefore as number) >= 0 &&
              value.inventoryQuantityAfter === value.inventoryQuantityBefore
            : value.inventoryQuantityBefore === null && value.inventoryQuantityAfter === null
        : value.approvalSnapshotVersion === 1 &&
          value.physicalDisposition === "SALEABLE_RETURNED" &&
          value.inventoryQuantityBefore === null &&
          value.inventoryQuantityAfter === null;
  return (
    allocationPlanValid &&
    bounded(value.itemId, 128) &&
    Number.isSafeInteger(value.returnQuantity) &&
    (value.returnQuantity as number) >= 1 &&
    (value.returnQuantity as number) <= 99 &&
    Number.isSafeInteger(value.amountFen) &&
    (value.amountFen as number) >= 1 &&
    (combinedRefund ||
      ["CASH", "STATIC_WECHAT", "STATIC_ALIPAY", "MEMBER_BALANCE", "WECHAT_PAY", "ALIPAY"].includes(
        String(value.paymentMethod),
      )) &&
    (combinedRefund ||
      [
        "LOCAL_CASH_CONFIRMATION",
        "MANUAL_STATIC_CODE_CONFIRMATION",
        "MEMBER_BALANCE_CLOUD_REFUND",
        "OFFICIAL_PROVIDER_REFUND",
      ].includes(String(value.refundExecutionMode))) &&
    Number.isSafeInteger(value.returnedQuantityAfter) &&
    (value.returnedQuantityAfter as number) >= (value.returnQuantity as number) &&
    (value.physicalDisposition === "SALEABLE_RETURNED" ||
      value.physicalDisposition === "UNSALEABLE_RETURNED") &&
    (value.inventoryEffect === "SALEABLE_STOCK_RESTORED" ||
      value.inventoryEffect === "NO_SALEABLE_STOCK_CHANGE") &&
    value.inventoryEffect ===
      (value.physicalDisposition === "SALEABLE_RETURNED"
        ? "SALEABLE_STOCK_RESTORED"
        : "NO_SALEABLE_STOCK_CHANGE") &&
    typeof value.inventorySnapshotKnown === "boolean" &&
    inventoryExpectationValid
  );
}

function validLegacyExpectedWithoutDisposition(value: unknown): value is Record<string, unknown> {
  return (
    record(value) &&
    exactKeys(value, [
      "itemId",
      "returnQuantity",
      "amountFen",
      "paymentMethod",
      "refundExecutionMode",
      "returnedQuantityAfter",
    ]) &&
    bounded(value.itemId, 128) &&
    Number.isSafeInteger(value.returnQuantity) &&
    (value.returnQuantity as number) >= 1 &&
    (value.returnQuantity as number) <= 99 &&
    Number.isSafeInteger(value.amountFen) &&
    (value.amountFen as number) >= 1 &&
    ["CASH", "STATIC_WECHAT", "STATIC_ALIPAY", "MEMBER_BALANCE", "WECHAT_PAY", "ALIPAY"].includes(
      String(value.paymentMethod),
    ) &&
    [
      "LOCAL_CASH_CONFIRMATION",
      "MANUAL_STATIC_CODE_CONFIRMATION",
      "MEMBER_BALANCE_CLOUD_REFUND",
      "OFFICIAL_PROVIDER_REFUND",
    ].includes(String(value.refundExecutionMode)) &&
    Number.isSafeInteger(value.returnedQuantityAfter) &&
    (value.returnedQuantityAfter as number) >= (value.returnQuantity as number)
  );
}

function validLegacyExpectedWithDisposition(value: unknown): boolean {
  return (
    record(value) &&
    exactKeys(value, [
      "itemId",
      "returnQuantity",
      "amountFen",
      "paymentMethod",
      "refundExecutionMode",
      "returnedQuantityAfter",
      "physicalDisposition",
      "inventoryEffect",
      "inventoryQuantityBefore",
      "inventoryQuantityAfter",
    ]) &&
    validExpected({
      ...value,
      approvalSnapshotVersion: 1,
      inventorySnapshotKnown: true,
    })
  );
}

function validLegacyRecovery(value: unknown): value is Record<string, unknown> {
  return (
    record(value) &&
    exactKeys(value, ["tenantId", "storeId", "accountId", "command", "expected", "state"]) &&
    bounded(value.tenantId, 512) &&
    bounded(value.storeId, 512) &&
    bounded(value.accountId, 512) &&
    isExecutePaidOrderItemReturnCommand(value.command) &&
    (validLegacyExpectedWithoutDisposition(value.expected) ||
      validLegacyExpectedWithDisposition(value.expected)) &&
    (value.state === "CLAIMED" || value.state === "OUTCOME_UNKNOWN")
  );
}

function migrateLegacyRecovery(
  value: Record<string, unknown>,
): PaidOrderItemReturnExecutionRecovery {
  const expected = value.expected as Record<string, unknown>;
  const migratedExpected = validLegacyExpectedWithDisposition(expected)
    ? {
        ...expected,
        approvalSnapshotVersion: 1 as const,
        inventorySnapshotKnown: true,
      }
    : {
        ...expected,
        approvalSnapshotVersion: 1 as const,
        physicalDisposition: "SALEABLE_RETURNED" as const,
        inventoryEffect: "SALEABLE_STOCK_RESTORED" as const,
        inventoryQuantityBefore: null,
        inventoryQuantityAfter: null,
        inventorySnapshotKnown: false,
      };
  return immutable(
    {
      tenantId: value.tenantId as string,
      storeId: value.storeId as string,
      id: value.accountId as string,
    },
    value.command as ExecutePaidOrderItemReturnCommand,
    migratedExpected as PaidOrderItemReturnExecutionExpected,
    value.state as PaidOrderItemReturnExecutionRecovery["state"],
  );
}

function validRecovery(value: unknown): value is PaidOrderItemReturnExecutionRecovery {
  return (
    record(value) &&
    exactKeys(value, ["tenantId", "storeId", "accountId", "command", "expected", "state"]) &&
    bounded(value.tenantId, 512) &&
    bounded(value.storeId, 512) &&
    bounded(value.accountId, 512) &&
    isExecutePaidOrderItemReturnCommand(value.command) &&
    validExpected(value.expected) &&
    (value.state === "CLAIMED" || value.state === "OUTCOME_UNKNOWN")
  );
}

function operatorKey(operator: { tenantId: string; storeId: string; id: string }): string {
  return JSON.stringify([operator.tenantId, operator.storeId, operator.id]);
}

function recoveryKey(recovery: PaidOrderItemReturnExecutionRecovery): string {
  return JSON.stringify([recovery.tenantId, recovery.storeId, recovery.accountId]);
}

function belongsTo(
  recovery: PaidOrderItemReturnExecutionRecovery,
  operator: { tenantId: string; storeId: string; id: string },
): boolean {
  return recoveryKey(recovery) === operatorKey(operator);
}

function immutable(
  operator: { tenantId: string; storeId: string; id: string },
  command: ExecutePaidOrderItemReturnCommand,
  expected: PaidOrderItemReturnExecutionExpected,
  state: PaidOrderItemReturnExecutionRecovery["state"],
): PaidOrderItemReturnExecutionRecovery {
  return Object.freeze({
    tenantId: operator.tenantId,
    storeId: operator.storeId,
    accountId: operator.id,
    command: Object.freeze(structuredClone(command)),
    expected: Object.freeze(structuredClone(expected)),
    state,
  });
}

function withState(
  recovery: PaidOrderItemReturnExecutionRecovery,
  state: PaidOrderItemReturnExecutionRecovery["state"],
): PaidOrderItemReturnExecutionRecovery {
  return Object.freeze({ ...recovery, state });
}

function same(
  left: PaidOrderItemReturnExecutionRecovery,
  right: PaidOrderItemReturnExecutionRecovery,
): boolean {
  return JSON.stringify(left) === JSON.stringify(right);
}

function storage(): Storage | null {
  if (typeof window === "undefined") return null;
  try {
    return typeof sessionStorage === "undefined" ? null : sessionStorage;
  } catch {
    return null;
  }
}

function readStored(target: Storage): readonly PaidOrderItemReturnExecutionRecovery[] | null {
  try {
    const encoded = target.getItem(PAID_ORDER_ITEM_RETURN_EXECUTION_RECOVERY_STORAGE_KEY);
    if (encoded !== null) {
      const value = JSON.parse(encoded) as unknown;
      if (
        !record(value) ||
        !exactKeys(value, ["version", "recoveries"]) ||
        value.version !== 2 ||
        !Array.isArray(value.recoveries) ||
        value.recoveries.length > 32 ||
        !value.recoveries.every(validRecovery)
      ) {
        return null;
      }
      const recoveries = value.recoveries.map((recovery) =>
        immutable(
          { tenantId: recovery.tenantId, storeId: recovery.storeId, id: recovery.accountId },
          recovery.command,
          recovery.expected,
          recovery.state,
        ),
      );
      return new Set(recoveries.map(recoveryKey)).size === recoveries.length ? recoveries : null;
    }
    const legacyEncoded = target.getItem(
      PAID_ORDER_ITEM_RETURN_EXECUTION_RECOVERY_LEGACY_STORAGE_KEY,
    );
    if (legacyEncoded === null) return [];
    const legacyValue = JSON.parse(legacyEncoded) as unknown;
    if (
      !record(legacyValue) ||
      !exactKeys(legacyValue, ["version", "recoveries"]) ||
      legacyValue.version !== 1 ||
      !Array.isArray(legacyValue.recoveries) ||
      legacyValue.recoveries.length > 32 ||
      !legacyValue.recoveries.every(validLegacyRecovery)
    ) {
      return null;
    }
    const recoveries = legacyValue.recoveries.map((recovery) =>
      migrateLegacyRecovery(recovery as Record<string, unknown>),
    );
    return new Set(recoveries.map(recoveryKey)).size === recoveries.length ? recoveries : null;
  } catch {
    return null;
  }
}

function saveAll(recoveries: readonly PaidOrderItemReturnExecutionRecovery[]): boolean {
  if (typeof window === "undefined") return true;
  const target = storage();
  if (!target) return false;
  try {
    if (recoveries.length === 0) {
      target.removeItem(PAID_ORDER_ITEM_RETURN_EXECUTION_RECOVERY_STORAGE_KEY);
      target.removeItem(PAID_ORDER_ITEM_RETURN_EXECUTION_RECOVERY_LEGACY_STORAGE_KEY);
      return (
        target.getItem(PAID_ORDER_ITEM_RETURN_EXECUTION_RECOVERY_STORAGE_KEY) === null &&
        target.getItem(PAID_ORDER_ITEM_RETURN_EXECUTION_RECOVERY_LEGACY_STORAGE_KEY) === null
      );
    }
    target.setItem(
      PAID_ORDER_ITEM_RETURN_EXECUTION_RECOVERY_STORAGE_KEY,
      JSON.stringify({ version: 2, recoveries }),
    );
    const stored = readStored(target);
    const saved =
      stored !== null &&
      stored.length === recoveries.length &&
      recoveries.every((recovery) => stored.some((candidate) => same(candidate, recovery)));
    if (!saved) return false;
    target.removeItem(PAID_ORDER_ITEM_RETURN_EXECUTION_RECOVERY_LEGACY_STORAGE_KEY);
    return target.getItem(PAID_ORDER_ITEM_RETURN_EXECUTION_RECOVERY_LEGACY_STORAGE_KEY) === null;
  } catch {
    return false;
  }
}

function hydrate(): void {
  if (hydrated) return;
  hydrated = true;
  if (typeof window === "undefined") return;
  const target = storage();
  if (!target) {
    unavailable = true;
    return;
  }
  const stored = readStored(target);
  if (stored === null) {
    unavailable = true;
    return;
  }
  const restored = stored.map((recovery) =>
    recovery.state === "CLAIMED" ? withState(recovery, "OUTCOME_UNKNOWN") : recovery,
  );
  if (!saveAll(restored)) {
    unavailable = true;
    return;
  }
  for (const recovery of restored) pendingByIdentity.set(recoveryKey(recovery), recovery);
}

export function readPaidOrderItemReturnExecutionRecovery(operator: {
  tenantId: string;
  storeId: string;
  id: string;
}): PaidOrderItemReturnExecutionRecoveryView {
  hydrate();
  if (unavailable) return { status: "RECOVERY_STORAGE_UNAVAILABLE" };
  const recovery = pendingByIdentity.get(operatorKey(operator));
  return recovery && belongsTo(recovery, operator)
    ? { status: "OWNED", recovery }
    : { status: "NONE" };
}

export function claimPaidOrderItemReturnExecutionRecovery(
  operator: { tenantId: string; storeId: string; id: string },
  command: ExecutePaidOrderItemReturnCommand,
  expected: PaidOrderItemReturnExecutionExpected,
): PaidOrderItemReturnExecutionRecoveryView {
  const current = readPaidOrderItemReturnExecutionRecovery(operator);
  if (current.status !== "NONE") return current;
  if (
    !bounded(operator.tenantId, 512) ||
    !bounded(operator.storeId, 512) ||
    !bounded(operator.id, 512) ||
    !isExecutePaidOrderItemReturnCommand(command) ||
    !validExpected(expected)
  ) {
    unavailable = true;
    return { status: "RECOVERY_STORAGE_UNAVAILABLE" };
  }
  const recovery = immutable(operator, command, expected, "CLAIMED");
  if (!saveAll([...pendingByIdentity.values(), recovery])) {
    unavailable = true;
    return { status: "RECOVERY_STORAGE_UNAVAILABLE" };
  }
  pendingByIdentity.set(operatorKey(operator), recovery);
  return { status: "OWNED", recovery };
}

function exactDurable(
  recovery: PaidOrderItemReturnExecutionRecovery,
  operator: { tenantId: string; storeId: string; id: string },
): boolean {
  hydrate();
  const key = operatorKey(operator);
  if (unavailable) return false;
  if (pendingByIdentity.get(key) !== recovery || !belongsTo(recovery, operator)) {
    return false;
  }
  if (typeof window === "undefined") return true;
  const target = storage();
  const stored = target ? readStored(target) : null;
  if (stored?.some((candidate) => same(candidate, recovery))) return true;
  unavailable = true;
  return false;
}

export function beginPaidOrderItemReturnExecutionDispatch(
  recovery: PaidOrderItemReturnExecutionRecovery,
  operator: { tenantId: string; storeId: string; id: string },
): boolean {
  const key = operatorKey(operator);
  if (dispatchingByIdentity.has(key) || !exactDurable(recovery, operator)) return false;
  dispatchingByIdentity.set(key, recovery);
  return true;
}

export function finishPaidOrderItemReturnExecutionDispatch(
  recovery: PaidOrderItemReturnExecutionRecovery,
  operator: { tenantId: string; storeId: string; id: string },
): boolean {
  const key = operatorKey(operator);
  if (dispatchingByIdentity.get(key) !== recovery || !belongsTo(recovery, operator)) return false;
  dispatchingByIdentity.delete(key);
  return true;
}

export function markPaidOrderItemReturnExecutionOutcomeUnknown(
  recovery: PaidOrderItemReturnExecutionRecovery,
  operator: { tenantId: string; storeId: string; id: string },
): boolean {
  if (!exactDurable(recovery, operator)) return false;
  const unknown =
    recovery.state === "OUTCOME_UNKNOWN" ? recovery : withState(recovery, "OUTCOME_UNKNOWN");
  const key = operatorKey(operator);
  const next = [...pendingByIdentity.values()].map((candidate) =>
    recoveryKey(candidate) === key ? unknown : candidate,
  );
  if (!saveAll(next)) {
    unavailable = true;
    return false;
  }
  pendingByIdentity.set(key, unknown);
  return true;
}

export function clearPaidOrderItemReturnExecutionRecovery(
  recovery: PaidOrderItemReturnExecutionRecovery,
  operator: { tenantId: string; storeId: string; id: string },
): boolean {
  if (!exactDurable(recovery, operator)) return false;
  const key = operatorKey(operator);
  const next = [...pendingByIdentity.values()].filter(
    (candidate) => recoveryKey(candidate) !== key,
  );
  if (!saveAll(next)) {
    unavailable = true;
    return false;
  }
  pendingByIdentity.delete(key);
  return true;
}

export function settlePaidOrderItemReturnExecutionRecovery(
  recovery: PaidOrderItemReturnExecutionRecovery,
  operator: { tenantId: string; storeId: string; id: string },
  executionStatus: ApprovalRequestReadModel["execution"]["status"],
): "CLEARED" | "RETAINED" | null {
  if (executionStatus === "SUCCEEDED") {
    return clearPaidOrderItemReturnExecutionRecovery(recovery, operator) ? "CLEARED" : null;
  }
  return markPaidOrderItemReturnExecutionOutcomeUnknown(recovery, operator) ? "RETAINED" : null;
}

export function paidOrderItemReturnExecutionOutcomeIsUnknown(error: unknown): boolean {
  const candidate = error as { code?: unknown; statusCode?: unknown } | null;
  const code = typeof candidate?.code === "string" ? candidate.code : null;
  const statusCode = typeof candidate?.statusCode === "number" ? candidate.statusCode : null;
  if (
    code === "GATEWAY_UNREACHABLE" ||
    code === "GATEWAY_RESPONSE_INVALID" ||
    code === "OPERATOR_IDENTITY_CHANGED"
  ) {
    return true;
  }
  if (statusCode === null) return true;
  return statusCode === 408 || statusCode === 429 || statusCode >= 500;
}

export function resetPaidOrderItemReturnExecutionRecoveryForTests(): void {
  pendingByIdentity.clear();
  dispatchingByIdentity.clear();
  hydrated = false;
  unavailable = false;
  try {
    storage()?.removeItem(PAID_ORDER_ITEM_RETURN_EXECUTION_RECOVERY_STORAGE_KEY);
    storage()?.removeItem(PAID_ORDER_ITEM_RETURN_EXECUTION_RECOVERY_LEGACY_STORAGE_KEY);
  } catch {
    // Test cleanup also clears the fail-closed in-memory state.
  }
}

export function resetPaidOrderItemReturnExecutionRecoveryMemoryForTests(): void {
  pendingByIdentity.clear();
  dispatchingByIdentity.clear();
  hydrated = false;
  unavailable = false;
}
